import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, Users, Target, TrendingUp } from 'lucide-react';
import { companyInfo } from '../data/mock';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      icon: Award,
      title: 'ISO Certified',
      description: 'Quality management system ensuring excellence in every project'
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Skilled professionals with 11+ years of combined experience'
    },
    {
      icon: Target,
      title: 'Client Focused',
      description: 'Dedicated to delivering solutions tailored to your needs'
    },
    {
      icon: TrendingUp,
      title: 'Growth Partner',
      description: 'Helping businesses scale with reliable IT infrastructure'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { type: 'spring', stiffness: 100, damping: 15 }
    }
  };

  return (
    <section id="about" className="about-section" ref={ref}>
      <div className="about-container">
        {/* Section Header */}
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span 
            className="section-tag"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            About Us
          </motion.span>
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            Pioneering IT Solutions
            <span className="gradient-text"> Since 2014</span>
          </motion.h2>
        </motion.div>

        <div className="about-content">
          {/* Left - Image/Visual */}
          <motion.div 
            className="about-visual"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4, type: 'spring', stiffness: 80 }}
          >
            <motion.div 
              className="about-card-main"
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 200 }}
            >
              <div className="card-glow"></div>
              <div className="card-content">
                <div className="company-logo-large">
                  <img 
                    src="/PR%20NETCOM%20Transparent%20BG.png"
                    alt="P.R. Net Com"
                    className="about-logo-img"
                  />
                </div>
                <div className="established-badge">
                  <motion.span 
                    className="badge-year"
                    whileHover={{ scale: 1.05 }}
                  >
                    Est. {companyInfo.established}
                  </motion.span>
                  <motion.span 
                    className="badge-cert"
                    whileHover={{ scale: 1.05 }}
                  >
                    {companyInfo.certification}
                  </motion.span>
                </div>
              </div>
            </motion.div>
            
            {/* Floating Elements */}
            <motion.div 
              className="float-element element-1"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 }}
              whileHover={{ scale: 1.1 }}
            >
              <Award size={24} />
              <span>Certified</span>
            </motion.div>
            <motion.div 
              className="float-element element-2"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1 }}
              whileHover={{ scale: 1.1 }}
            >
              <span className="exp-number">11+</span>
              <span>Years Exp</span>
            </motion.div>
          </motion.div>

          {/* Right - Content */}
          <motion.div 
            className="about-text"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <motion.p className="about-intro" variants={itemVariants}>
              <strong>P.R. NET COM</strong> is a Kolkata-based, ISO 9001:2008 certified 
              proprietorship established in 2014, providing comprehensive IT solutions 
              to businesses across India.
            </motion.p>
            
            <motion.p className="about-description" variants={itemVariants}>
              Under the visionary leadership of <strong>{companyInfo.proprietor}</strong>, 
              we have grown to become a trusted partner for major financial institutions 
              including UCO Bank, SBI, and numerous government organizations. Our expertise 
              spans hardware sales, networking, maintenance services, and complete IT 
              infrastructure management.
            </motion.p>

            <motion.div 
              className="about-features"
              variants={containerVariants}
            >
              {features.map((feature, index) => (
                <motion.div 
                  key={index} 
                  className="feature-item"
                  variants={itemVariants}
                  whileHover={{ x: 10 }}
                  data-testid={`feature-${index}`}
                >
                  <motion.div 
                    className="feature-icon"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <feature.icon size={24} />
                  </motion.div>
                  <div className="feature-content">
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
